<script setup>
import { BBadge, BButton, BCard, BCardHeader, BOverlay, BPagination, BProgress, vBTooltip } from 'bootstrap-vue-next';
import { computed } from 'vue';

import TableWithBusyState from '~/common/components/TableWithBusyState.vue';
import { dateFormatter } from '~/common/FieldFormats';

import { issueIid, projectPath, topLevelGroup } from './projects';

const props = defineProps({
  issues: {
    type: Array,
    required: true,
  },
  shownLabels: {
    type: Array,
    required: true,
  },
  busy: {
    type: Boolean,
    required: true,
  },
  progress: {
    type: Number,
    default: 3,
  },
  totalRows: {
    type: Number,
    default: null,
  },
});

const serverPaging = computed(() => props.totalRows !== null);

const sortBy = defineModel('sortBy', {
  type: Array,
  required: true,
});
const currentPage = defineModel('currentPage', {
  type: Number,
  required: true,
});

const perPage = 20;

const getVisibleLabels = (issue, shownLabels) => {
  return issue.labels.nodes.filter(label => shownLabels.includes(label.title));
};

const fields = [
  {
    key: 'title',
    label: 'Issue',
    description: 'Issue title with link to GitLab',
    sortable: true,
    class: 'text-break issue-title-cell',
  },
  {
    key: 'labels',
    label: 'Labels',
    description: 'Filtered labels matching your selection',
    sortable: false,
    class: 'issue-labels-cell',
  },
  {
    key: 'category',
    label: 'Category',
    description: 'Product category from labels, or the GitLab group for issues without one',
    sortable: false,
    class: 'issue-category-cell',
  },
  {
    key: 'createdAt',
    label: 'Created',
    description: 'Date the issue was created',
    sortable: true,
    class: 'issue-created-cell',
    formatter: ({ value }) => dateFormatter(value),
  },
  {
    key: 'actions',
    label: '',
    sortable: false,
    class: 'text-end',
  },
];

const getCategory = (issue) => {
  for (const label of issue.labels.nodes) {
    if (label.title === 'Contributor Success') {
      return 'Contributor Success';
    }

    if (label.title.startsWith('Category:')) {
      return label.title.slice(9).trim();
    }
  }

  return topLevelGroup(issue);
};

const items = computed(() => {
  return props.issues.map(issue => ({
    ...issue,
    category: getCategory(issue),
    visibleLabels: getVisibleLabels(issue, props.shownLabels),
  }));
});

const effectiveTotalRows = computed(() => props.totalRows ?? items.value.length);
</script>

<template>
  <div
    v-if="busy"
    class="py-5"
  >
    <BOverlay
      :show="busy"
    >
      <template #overlay>
        <div class="text-center">
          <BProgress
            :value="progress"
          />
          <p>Loading issues... {{ progress }}%</p>
        </div>
      </template>
    </BOverlay>
  </div>
  <BCard
    v-else
    class="my-2"
    no-body
  >
    <BCardHeader class="p-3 accordion-style-card-header">
      <h2 class="m-1">Issues ({{ effectiveTotalRows }})</h2>
    </BCardHeader>
    <TableWithBusyState
      v-model:sort-by="sortBy"
      :fields="fields"
      :items="items"
      :busy="busy"
      :per-page="serverPaging ? undefined : perPage"
      :current-page="serverPaging ? undefined : currentPage"
      :no-local-sorting="serverPaging"
      show-empty
      hover
    >
      <template
        v-for="field in fields"
        :key="field.key"
        #[`head(${field.key})`]
      >
        <span
          v-if="field.label"
          v-b-tooltip="{ title: field.description, delay: { hide: 0 } }"
          class="with-help-text"
          tabindex="0"
          :aria-label="field.description"
        >
          {{ field.label }}
        </span>
      </template>
      <template #cell(title)="{ item }">
        <a
          :href="item.webUrl"
          target="_blank"
          class="fw-medium"
        >
          {{ item.title }}
        </a>
      </template>
      <template #cell(labels)="{ item }">
        <BBadge
          v-for="label in item.visibleLabels"
          :key="label.title"
          pill
          variant="secondary"
          class="me-1"
        >
          {{ label.title }}
        </BBadge>
      </template>
      <template #cell(actions)="{ item }">
        <BButton
          :to="{ name: 'manage-issue', query: { projectPath: projectPath(item), issueIid: issueIid(item), action: 'work' } }"
          variant="outline-primary"
          class="text-nowrap"
          size="sm"
        >
          Assign issue
        </BButton>
      </template>
    </TableWithBusyState>
    <BPagination
      v-if="effectiveTotalRows > perPage"
      v-model="currentPage"
      :total-rows="effectiveTotalRows"
      :per-page="perPage"
      align="center"
      first-number
      last-number
    />
  </BCard>
</template>
